// ─────────────────────────────────────────────────────────────
// Vue d'ensemble du tableau de bord vendeur.
// Résumé : contenus (produits/services/annonces), commandes,
// revenus, mini-graphique de ventes et actions rapides.
// ─────────────────────────────────────────────────────────────

import { useMemo, useState } from 'react';
import VendorNotificationsFeed from './VendorNotificationsFeed';
import type { VendorReservationStats } from '@/lib/vendorNotifications';

export interface OverviewCommande {
  id: number;
  totalttc: string | null;
  statut_com_vendeur: string | null;
  date_time: string | null;
}

interface VendorOverviewProps {
  counts: { produits: number; services: number; annonces: number; publies: number };
  commandes: OverviewCommande[];
  currency: string;
  pendingCount: number;
  /** Id du vendeur (pour le fil de notifications / activité IA). */
  vendorId?: number;
  /** Réservations des services du vendeur (comptabilisées dans les stats). */
  reservations?: VendorReservationStats;
  onGoto: (tab: 'produits' | 'services' | 'annonces' | 'reservations' | 'categorie') => void;
  onNewAnnonce: (type: string) => void;
}

function StatCard({
  icon,
  label,
  value,
  tone,
  onClick,
}: {
  icon: string;
  label: string;
  value: string | number;
  tone: string;
  onClick?: () => void;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      className={`bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 text-left ${
        onClick ? 'cursor-pointer hover:border-background-300/60 transition-colors' : ''
      }`}
    >
      <div className={`w-10 h-10 rounded-lg ${tone} flex items-center justify-center mb-3`}>
        <i className={`${icon} text-lg`}></i>
      </div>
      <div className="text-2xl font-bold font-heading text-foreground-950 tabular-nums">{value}</div>
      <div className="text-xs text-foreground-500 mt-0.5">{label}</div>
    </Tag>
  );
}

// ── Mini-graphique de ventes (7 / 30 jours) ────────────────────

const toDayKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

interface DayBucket {
  key: string;
  date: Date;
  revenue: number;
  count: number;
}

function useDailySeries(commandes: OverviewCommande[], days: number): DayBucket[] {
  return useMemo(() => {
    const now = new Date();
    const buckets: DayBucket[] = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      buckets.push({ key: toDayKey(d), date: d, revenue: 0, count: 0 });
    }
    const index = new Map(buckets.map((b, i) => [b.key, i]));
    commandes.forEach((c) => {
      if (!c.date_time) return;
      const d = new Date(c.date_time);
      if (Number.isNaN(d.getTime())) return;
      const i = index.get(toDayKey(d));
      if (i == null) return;
      const amount = parseFloat(String(c.totalttc ?? '0'));
      buckets[i].revenue += Number.isNaN(amount) ? 0 : amount;
      buckets[i].count += 1;
    });
    return buckets;
  }, [commandes, days]);
}

function SalesChart({ commandes, currency }: { commandes: OverviewCommande[]; currency: string }) {
  const [days, setDays] = useState<7 | 30>(7);
  const series = useDailySeries(commandes, days);

  const totals = useMemo(() => {
    const revenue = series.reduce((s, d) => s + d.revenue, 0);
    const orders = series.reduce((s, d) => s + d.count, 0);
    return { revenue, orders, average: revenue / days };
  }, [series, days]);

  const max = Math.max(1, ...series.map((d) => d.revenue));
  const bestKey = series.reduce((best, d) => (d.revenue > (best?.revenue ?? -1) ? d : best), series[0])?.key;

  const fmtDate = (d: Date) =>
    d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
          <i className="ri-line-chart-line text-primary-500"></i>
          Ventes
        </h3>
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
          {([7, 30] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                days === d ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              {d} jours
            </button>
          ))}
        </div>
      </div>

      {/* Résumé de la période */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div>
          <div className="text-lg font-bold text-foreground-950 tabular-nums">
            {Math.round(totals.revenue).toLocaleString()} {currency}
          </div>
          <div className="text-[11px] text-foreground-500">Revenus ({days}j)</div>
        </div>
        <div>
          <div className="text-lg font-bold text-foreground-950 tabular-nums">{totals.orders}</div>
          <div className="text-[11px] text-foreground-500">Commandes</div>
        </div>
        <div>
          <div className="text-lg font-bold text-foreground-950 tabular-nums">
            {Math.round(totals.average).toLocaleString()} {currency}
          </div>
          <div className="text-[11px] text-foreground-500">Moyenne / jour</div>
        </div>
      </div>

      {/* Barres */}
      <div className="flex items-end gap-[3px] h-28">
        {series.map((d) => {
          const ratio = d.revenue / max;
          const height = d.revenue > 0 ? Math.max(6, Math.round(ratio * 100)) : 3;
          const isBest = d.key === bestKey && d.revenue > 0;
          return (
            <div
              key={d.key}
              title={`${fmtDate(d.date)} · ${Math.round(d.revenue).toLocaleString()} ${currency} · ${d.count} cmd`}
              className="flex-1 flex items-end h-full"
            >
              <div
                className={`w-full rounded-t transition-all duration-300 ${
                  isBest ? 'bg-accent-500' : 'bg-primary-500/70 hover:bg-primary-500'
                }`}
                style={{ height: `${height}%` }}
              ></div>
            </div>
          );
        })}
      </div>

      {/* Axe des dates */}
      <div className="flex items-center justify-between mt-2 text-[10px] text-foreground-400">
        <span>{fmtDate(series[0].date)}</span>
        <span>{fmtDate(series[series.length - 1].date)}</span>
      </div>
    </div>
  );
}

export default function VendorOverview({
  counts,
  commandes,
  currency,
  pendingCount,
  vendorId,
  reservations,
  onGoto,
  onNewAnnonce,
}: VendorOverviewProps) {
  // Les réservations sont converties en « événements de revenu » pour être
  // comptabilisées dans le chiffre d'affaires, le graphique et les compteurs.
  const reservationEvents = useMemo<OverviewCommande[]>(
    () =>
      (reservations?.rows || [])
        .filter((r) => r.date)
        .map((r) => ({
          id: -r.id,
          totalttc: String(r.amount),
          statut_com_vendeur: 'Réservation',
          date_time: r.date,
        })),
    [reservations],
  );
  const revenueEvents = useMemo(
    () => [...commandes, ...reservationEvents],
    [commandes, reservationEvents],
  );
  const reservationCount = reservations?.count ?? 0;
  const reservationAmount = reservations?.amount ?? 0;

  const revenue = revenueEvents.reduce((sum, c) => {
    const n = parseFloat(String(c.totalttc ?? '0'));
    return sum + (Number.isNaN(n) ? 0 : n);
  }, 0);
  const total = counts.produits + counts.services + counts.annonces;
  const recent = revenueEvents.slice(0, 5);
  void total;

  return (
    <div className="space-y-6">
      {/* Compteur d'attente */}
      {pendingCount > 0 && (
        <div className="px-4 py-3 rounded-lg bg-accent-100/70 border border-accent-200 text-xs text-accent-900 flex items-center gap-2">
          <i className="ri-time-line"></i>
          {pendingCount} commande{pendingCount > 1 ? 's' : ''} en attente de traitement.
          <button onClick={() => onGoto('reservations')} className="ml-auto underline cursor-pointer">Voir</button>
        </div>
      )}

      {/* Statistiques principales */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <StatCard icon="ri-shopping-bag-3-line" label="Produits" value={counts.produits} tone="bg-primary-100 text-primary-700" onClick={() => onGoto('produits')} />
        <StatCard icon="ri-service-line" label="Services" value={counts.services} tone="bg-secondary-100 text-secondary-900" onClick={() => onGoto('services')} />
        <StatCard icon="ri-megaphone-line" label="Annonces" value={counts.annonces} tone="bg-accent-100 text-accent-700" onClick={() => onGoto('annonces')} />
        <StatCard icon="ri-checkbox-circle-line" label="Publiés" value={counts.publies} tone="bg-primary-100 text-primary-700" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-money-dollar-circle-line text-xl"></i>
          </div>
          <div>
            <div className="text-xl font-bold text-foreground-950 tabular-nums">
              {Math.round(revenue).toLocaleString()} {currency}
            </div>
            <div className="text-xs text-foreground-500">Revenus (commandes + réservations)</div>
          </div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
            <i className="ri-shopping-cart-2-line text-xl"></i>
          </div>
          <div>
            <div className="text-xl font-bold text-foreground-950 tabular-nums">{commandes.length}</div>
            <div className="text-xs text-foreground-500">Commandes reçues</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onGoto('reservations')}
          className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 flex items-center gap-4 text-left cursor-pointer hover:border-background-300/60 transition-colors"
        >
          <div className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-calendar-check-line text-xl"></i>
          </div>
          <div className="min-w-0">
            <div className="text-xl font-bold text-foreground-950 tabular-nums">{reservationCount}</div>
            <div className="text-xs text-foreground-500 truncate">
              Réservations{reservationAmount > 0 ? ` · ${Math.round(reservationAmount).toLocaleString()} ${currency}` : ''}
            </div>
          </div>
        </button>
      </div>

      {/* Mini-graphique de ventes (commandes + réservations) */}
      <SalesChart commandes={revenueEvents} currency={currency} />

      {/* Notifications : commandes (dont IA), réservations et consultations IA */}
      {vendorId ? <VendorNotificationsFeed vendorId={vendorId} title="Notifications & activité" /> : null}

      {/* Actions rapides */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
          <i className="ri-flashlight-line text-primary-500"></i>
          Actions rapides
        </h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onNewAnnonce('produits')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Ajouter un produit
          </button>
          <button
            onClick={() => onNewAnnonce('services')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-secondary-100 text-secondary-900 text-sm font-semibold hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Ajouter un service
          </button>
          <button
            onClick={() => onNewAnnonce('annonces')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-accent-100 text-accent-900 text-sm font-semibold hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Ajouter une annonce
          </button>
          <button
            onClick={() => onGoto('categorie')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-price-tag-3-line"></i>
            Ma catégorie
          </button>
        </div>
      </div>

      {/* Commandes récentes */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-background-200/70">
          <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
            <i className="ri-history-line text-primary-500"></i>
            Commandes récentes
          </h3>
          <button onClick={() => onGoto('reservations')} className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
            Tout voir <i className="ri-arrow-right-line"></i>
          </button>
        </div>
        {recent.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm text-foreground-500">Aucune commande pour le moment.</p>
          </div>
        ) : (
          <ul className="divide-y divide-background-200/70">
            {recent.map((c) => (
              <li key={c.id} className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground-900">
                    Commande #{c.id}
                  </div>
                  <div className="text-[11px] text-foreground-400">
                    {c.date_time ? new Date(c.date_time).toLocaleDateString('fr-FR') : '—'}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-foreground-900">
                    {c.totalttc ? `${Number(c.totalttc).toLocaleString()} ${currency}` : '—'}
                  </span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                    {c.statut_com_vendeur || 'En attente'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}